import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import RefreshIcon from "@mui/icons-material/Refresh";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import {
  ResponsiveTable,
  MobileCard,
  TableCardSkeleton,
} from "@/components/molecules/ResponsiveTable";
import {
  useTeamMembers,
  useTeamInvitations,
  useUpdateMemberRole,
  useRemoveMember,
  useResendTeamInvitation,
  useCancelTeamInvitation,
} from "../../hooks/team/use-team";
import { InviteTeamMemberForm } from "../../components/team/InviteTeamMemberForm";
import { TeamAnalytics } from "./TeamAnalytics";
import { useAuthContext } from "../../components/auth";
import type { TeamMember, Invitation } from "@property-reports/api-contract";

const invitationStatusColorMap: Record<
  string,
  "warning" | "success" | "error" | "default"
> = {
  pending: "warning",
  accepted: "success",
  canceled: "error",
  expired: "default",
};

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return `${date.toLocaleDateString("en-NZ")} ${date.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}`;
}

export function TeamManagement() {
  const [activeTab, setActiveTab] = useState(0);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const { isSuperAdmin, user } = useAuthContext();
  const { data: members, isLoading: membersLoading } = useTeamMembers();
  const { data: invitations, isLoading: invitationsLoading } =
    useTeamInvitations();

  // Org admins can only see members they can manage (not owners)
  const visibleMembers = isSuperAdmin
    ? members
    : members?.filter((m) => m.role !== "owner");

  // Permission helpers (DRY - single source of truth for role hierarchy)
  const canManage = (member: { role: string }): boolean => {
    if (member.role === "owner") return false;
    if (!isSuperAdmin && member.role === "admin") return false;
    return true;
  };

  const canDelete = (member: { role: string; userId: string }): boolean =>
    canManage(member) && member.userId !== user?.id;

  const canChangeRole = canManage;

  const updateRoleMutation = useUpdateMemberRole();
  const removeMemberMutation = useRemoveMember();
  const resendMutation = useResendTeamInvitation();
  const cancelMutation = useCancelTeamInvitation();

  const handleRoleChange = (memberId: string, newRole: "admin" | "member") => {
    updateRoleMutation.mutate({ memberId, role: newRole });
  };

  const handleRemoveMember = (memberId: string, email: string) => {
    if (
      window.confirm(`Are you sure you want to remove ${email} from the team?`)
    ) {
      removeMemberMutation.mutate(memberId);
    }
  };

  const handleResendInvitation = (invitationId: string) => {
    resendMutation.mutate(invitationId);
  };

  const handleCancelInvitation = (invitationId: string) => {
    if (window.confirm("Are you sure you want to cancel this invitation?")) {
      cancelMutation.mutate(invitationId);
    }
  };

  // Filter to only show pending/expired invitations
  const pendingInvitations = invitations?.filter(
    (inv) => inv.status === "pending" || inv.status === "expired",
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Team
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your organization&apos;s team members
          </Typography>
        </Box>
        {activeTab === 0 && (
          <Button
            variant="contained"
            onClick={() => setShowInviteForm(!showInviteForm)}
          >
            {showInviteForm ? "Cancel" : "Invite Member"}
          </Button>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab icon={<PeopleIcon />} iconPosition="start" label="Members" />
        <Tab icon={<BarChartIcon />} iconPosition="start" label="Analytics" />
      </Tabs>

      {activeTab === 1 && <TeamAnalytics />}

      {activeTab === 0 && (
        <>
          {showInviteForm && (
            <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Invite New Team Member
              </Typography>
              <InviteTeamMemberForm
                onSuccess={() => setShowInviteForm(false)}
              />
            </Card>
          )}

          {/* Pending Invitations Section */}
          {(invitationsLoading ||
            (pendingInvitations && pendingInvitations.length > 0)) && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight={600}
                color="text.primary"
                sx={{ mb: 2 }}
              >
                Pending Invitations
              </Typography>

              <Card variant="outlined" sx={{ overflow: "hidden" }}>
                <ResponsiveTable<Invitation>
                  data={pendingInvitations ?? []}
                  loading={invitationsLoading}
                  loadingSkeleton={
                    <TableCardSkeleton fieldCount={2} showStatus showActions />
                  }
                  getKey={(inv) => inv.id}
                  columns={[
                    {
                      key: "email",
                      header: "Email",
                      render: (inv) => (
                        <Typography variant="body2">{inv.email}</Typography>
                      ),
                    },
                    {
                      key: "role",
                      header: "Role",
                      render: (inv) => (
                        <Chip
                          label={inv.role}
                          size="small"
                          variant="outlined"
                        />
                      ),
                    },
                    {
                      key: "status",
                      header: "Status",
                      render: (inv) => (
                        <Chip
                          label={inv.status}
                          color={
                            invitationStatusColorMap[inv.status] ?? "default"
                          }
                          size="small"
                          variant="outlined"
                        />
                      ),
                    },
                    {
                      key: "expires",
                      header: "Expires",
                      render: (inv) => (
                        <Typography
                          variant="body2"
                          color={
                            inv.status === "expired"
                              ? "error.main"
                              : "text.secondary"
                          }
                        >
                          {formatDateTime(inv.expiresAt)}
                        </Typography>
                      ),
                    },
                    {
                      key: "actions",
                      header: "Actions",
                      align: "right",
                      render: (inv) => (
                        <>
                          <Tooltip title="Resend invitation">
                            <IconButton
                              size="small"
                              onClick={() => handleResendInvitation(inv.id)}
                              disabled={resendMutation.isPending}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel invitation">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancelInvitation(inv.id)}
                              disabled={cancelMutation.isPending}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ),
                    },
                  ]}
                  mobileCardRender={(inv) => (
                    <MobileCard
                      title={inv.email}
                      fields={[
                        {
                          label: "Role",
                          value: (
                            <Chip
                              label={inv.role}
                              size="small"
                              variant="outlined"
                            />
                          ),
                        },
                        {
                          label: "Expires",
                          value: (
                            <Typography
                              variant="body2"
                              color={
                                inv.status === "expired"
                                  ? "error.main"
                                  : "text.secondary"
                              }
                            >
                              {formatDateTime(inv.expiresAt)}
                            </Typography>
                          ),
                        },
                      ]}
                      status={
                        <Chip
                          label={inv.status}
                          color={
                            invitationStatusColorMap[inv.status] ?? "default"
                          }
                          size="small"
                          variant="outlined"
                        />
                      }
                      actions={
                        <>
                          <Tooltip title="Resend invitation">
                            <IconButton
                              size="small"
                              onClick={() => handleResendInvitation(inv.id)}
                              disabled={resendMutation.isPending}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel invitation">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleCancelInvitation(inv.id)}
                              disabled={cancelMutation.isPending}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      }
                    />
                  )}
                />
              </Card>
            </Box>
          )}

          {/* Team Members Section */}
          <Typography
            variant="h6"
            fontWeight={600}
            color="text.primary"
            sx={{ mb: 2 }}
          >
            Team Members
          </Typography>

          <Card variant="outlined" sx={{ overflow: "hidden" }}>
            <ResponsiveTable<TeamMember>
              data={visibleMembers ?? []}
              loading={membersLoading}
              loadingSkeleton={
                <TableCardSkeleton fieldCount={3} showStatus showActions />
              }
              getKey={(m) => m.id}
              columns={[
                {
                  key: "email",
                  header: "Email",
                  render: (m) => (
                    <Typography variant="body2">{m.email}</Typography>
                  ),
                },
                {
                  key: "name",
                  header: "Name",
                  render: (m) => (
                    <Typography variant="body2" color="text.secondary">
                      {m.firstName || m.lastName
                        ? `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim()
                        : m.name || "-"}
                    </Typography>
                  ),
                },
                {
                  key: "role",
                  header: "Role",
                  render: (m) =>
                    canChangeRole(m) ? (
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={m.role}
                          onChange={(e) =>
                            handleRoleChange(
                              m.id,
                              e.target.value as "admin" | "member",
                            )
                          }
                          disabled={updateRoleMutation.isPending}
                          size="small"
                        >
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="member">Member</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        label={m.role === "owner" ? "Owner" : m.role}
                        color={m.role === "owner" ? "secondary" : "default"}
                        size="small"
                        variant="outlined"
                      />
                    ),
                },
                {
                  key: "status",
                  header: "Status",
                  render: (m) => (
                    <Chip
                      label={m.isActive ? "Active" : "Suspended"}
                      color={m.isActive ? "success" : "error"}
                      size="small"
                      variant="outlined"
                    />
                  ),
                },
                {
                  key: "joined",
                  header: "Joined",
                  render: (m) => (
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(m.joinedAt)}
                    </Typography>
                  ),
                },
                {
                  key: "actions",
                  header: "Actions",
                  align: "right",
                  render: (m) =>
                    canDelete(m) ? (
                      <Tooltip title="Remove from team">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveMember(m.id, m.email)}
                          disabled={removeMemberMutation.isPending}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null,
                },
              ]}
              mobileCardRender={(m) => {
                const memberName =
                  m.firstName || m.lastName
                    ? `${m.firstName ?? ""} ${m.lastName ?? ""}`.trim()
                    : m.name || "-";

                return (
                  <MobileCard
                    title={m.email}
                    subtitle={memberName !== "-" ? memberName : undefined}
                    fields={[
                      {
                        label: "Role",
                        value: canChangeRole(m) ? (
                          <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                              value={m.role}
                              onChange={(e) =>
                                handleRoleChange(
                                  m.id,
                                  e.target.value as "admin" | "member",
                                )
                              }
                              disabled={updateRoleMutation.isPending}
                              size="small"
                            >
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="member">Member</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={m.role === "owner" ? "Owner" : m.role}
                            color={m.role === "owner" ? "secondary" : "default"}
                            size="small"
                            variant="outlined"
                          />
                        ),
                      },
                      {
                        label: "Joined",
                        value: formatDateTime(m.joinedAt),
                      },
                    ]}
                    status={
                      <Chip
                        label={m.isActive ? "Active" : "Suspended"}
                        color={m.isActive ? "success" : "error"}
                        size="small"
                        variant="outlined"
                      />
                    }
                    actions={
                      canDelete(m) ? (
                        <Tooltip title="Remove from team">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMember(m.id, m.email)}
                            disabled={removeMemberMutation.isPending}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  />
                );
              }}
            />
          </Card>
        </>
      )}
    </Box>
  );
}
